import React from "react";

import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { Chip, SxProps, Theme } from "@mui/material";
import { useReportGeneratorContext } from "components/Report-generator/context/ReportGenerator-context";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useRenderDateValue } from "hooks";
import type { CardConfig } from "store/types";

interface Props {
  config: CardConfig;
  sx?: SxProps<Theme>;
  // In preview/export the tooltip is suppressed — the chip is purely informational there.
  isPreview?: boolean;
}

// Relative ranges whose end is "now" / the last NAV date. In a generated PDF the label alone (e.g.
// "ITD") doesn't say which date it covered, so we annotate these with the report's as-of date.
const RELATIVE_TO_NOW = new Set(["ytd", "fromInception", "last3Years", "last5Years", "inceptionToLastNAV"]);

// Chip showing the time range applied to a single card (its per-card override of the report-level
// range). Renders nothing when the card has no applied range. Shared by the editable header and the
// preview/export header so both stay in sync.
export const CardTimeRangeChip: React.FC<Props> = ({ config, sx, isPreview }) => {
  const { reportMeta, dateRange: globalDateRange } = useReportGeneratorContext();
  const renderDateValue = useRenderDateValue();
  const dateRange = "cardFilters" in config ? config.cardFilters?.dateRange : undefined;

  // The chip marks a per-card override of the report-level range. Compare by the rendered
  // label (relative ranges like ITD/YTD render from `value` alone; absolute ones encode their
  // dates) — cached from/to can differ for the same logical range, so a raw object compare
  // would leave the chip on after the global range was changed to match the card.
  const cardLabel = dateRange ? renderDateValue(dateRange) : "";
  const globalLabel = globalDateRange ? renderDateValue(globalDateRange) : "";
  const sameAsGlobal = !!dateRange && cardLabel === globalLabel;

  const baseLabel = sameAsGlobal ? "" : cardLabel;

  if (!baseLabel) return null;

  // Append the report's as-of date for now-relative ranges so the exported PDF is unambiguous.
  const label =
    dateRange && reportMeta.asOfDate && RELATIVE_TO_NOW.has(dateRange.value)
      ? `${baseLabel} · ${reportMeta.asOfDate}`
      : baseLabel;

  return (
    <MaterialTooltip
      variant="dark"
      isShow={!isPreview}
      title="Date range applied to this card (overrides the report-level range)"
    >
      <Chip
        icon={<DateRangeOutlinedIcon />}
        label={label}
        size="small"
        sx={[
          {
            px: "4px",
            height: 20,
            bgcolor: "#e2e8f0",
            color: "#496682",
            ".MuiChip-label": { px: "6px", fontSize: 11, fontWeight: 500 },
            ".MuiChip-icon": { ml: "6px", mr: "-2px", color: "#496682" }
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
        ]}
      />
    </MaterialTooltip>
  );
};
